import { atom } from "jotai";
import { Node, Edge } from "@xyflow/react";
import { addError } from "@zapier/observability";

import { saveCanvasSetter } from "../persistence";
import { canvasAtom, canvasIdAtom, streamingProgressAtom } from "../canvas";
import { nodesAtom } from "../nodes";
import { edgesAtom } from "../edges";
import { isViewOnlyModeAtom } from "../permissions";
import {
  assignAssetsAndAttributionsToCanvas,
  convertAssetIdsToPlaceholderIds,
  removeTemplateFromString,
  attributeBackendNodesByType,
  isValidUUID,
} from "../utils";
import { ZSLFromTemplate } from "../types";
import { zslAtom } from "./zslAtom";
import { showCanvasTemplateWidgetAtom } from "./showCanvasTemplateWidgetAtom";
import { isExecutingZslAtom } from "./isExecutingZslAtom";
import { handleZslExecutionErrorSetter } from "./handleZslExecutionErrorSetter";
import { UIMessage, ComposerContextType } from "@zapier/agent-system";
import { programmaticNavigationExpectedAtom } from "../navigation";
import { startTourSetter } from "../tour";

type CanvasToSave = {
  nodes: Node[];
  edges: Edge[];
  name?: string;
  description?: string;
};

type Params = {
  prompt?: string;
  composerService: NonNullable<ComposerContextType>;
};

// Helper function to create messages with tool calls
const createMessage = (
  isError: boolean,
  errorMessage?: string,
  zslData?: any,
): UIMessage[] => {
  const messageText = isError
    ? "whoops looks like there is bad data on the canvas, let me fix that for you"
    : "Successfully executed ZSL template";

  const resultText = isError
    ? `failed due to: ${errorMessage}`
    : "Successfully executed ZSL template";

  return [
    {
      role: "assistant",
      content: messageText,
      id: `${isError ? "error" : "success"}-${Date.now()}`,
      createdAt: new Date(),
      parts: [
        { type: "text", text: messageText },
        {
          type: "tool-invocation",
          toolInvocation: {
            state: "result",
            toolName: "execute_zsl",
            toolCallId: `execute_zsl-${Date.now()}`,
            args: {
              zsl: zslData || {},
            },
            result: {
              content: [
                {
                  type: "text",
                  text: resultText,
                },
              ],
            },
          },
        },
      ],
    },
  ];
  // { id: "user-1", role: "user", content: "Fix the error in my canvas.", parts: [{type: "text", text: "Fix the error in my canvas."}]}
  // ;
};

// Helper function to add message to thread and submit it
const addMessageToThread = (
  // Use the composerService instead of jotai set function
  composerService: ComposerContextType,
  isError: boolean,
  errorMessage?: string,
  zslData?: any,
) => {
  if (!composerService) {
    return;
  }
  // Add message to thread
  const nextMessages = createMessage(isError, errorMessage, zslData);
  composerService.setMessages((prevMessages: UIMessage[]) => [
    ...prevMessages,
    ...nextMessages,
  ]);

  // Submit message thread if there was an error
  if (isError) {
    composerService.handleSubmit({
      target: { value: "Fix the error in my canvas." },
    });
  }
};

function trimString(inputString: string) {
  const maxLength = 99; // Canvas description must NOT have more than 100 characters
  const ellipsis = "...";

  if (inputString.length > maxLength) {
    return inputString.substring(0, maxLength - ellipsis.length) + ellipsis;
  }

  return inputString;
}

/* Converts template to real canvas nodes and edges
 * Used by CreateCanvasFromTemplateToolbar, AiCopilotBanner, template page, and AI page
 */
export const executeZslAndSaveSetter = atom(
  null,
  async (get, set, params: Params) => {
    const { prompt = "", composerService } = params;

    // Get the current canvas state
    const zsl = get(zslAtom);
    if (zsl === undefined) {
      return;
    }

    const canvasId = get(canvasIdAtom);
    set(isExecutingZslAtom, true);
    let processedZsl = { ...zsl };

    // For template or AI pages with no real canvas
    // nodes and edges will need to be backend versions since zsl-api uses v2 API to create a canvas
    if (!canvasId || !isValidUUID(canvasId)) {
      // Use the sendMessageAtom instead of direct composerService
      if (zsl) {
        if (zsl.canvases && zsl.canvases.length > 0) {
          // For template page, there will be canvases
          const processedCanvases = zsl.canvases.map((canvas) => {
            return {
              ...canvas,
              nodes: attributeBackendNodesByType(canvas.nodes, "TEMPLATE"),
            };
          });

          processedZsl = {
            ...processedZsl,
            canvases: processedCanvases,
          };
        } else {
          // For AI page, there will be NO canvases
          const nodes = get(nodesAtom);
          const edges = get(edgesAtom);

          const { nodes: backendNodes, edges: backendEdges } =
            convertAssetIdsToPlaceholderIds({ nodes, edges });

          processedZsl = {
            ...processedZsl,
            canvases: [
              {
                id: "_CANVAS_1",
                name: zsl.name,
                description: trimString(zsl.description || ""),
                nodes: backendNodes,
                edges: backendEdges,
              },
            ],
          };
        }

        try {
          const zslRes = await fetch("/api/canvasui/zsl", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              template: {
                meta: {},
                ...processedZsl,
              },
            }),
          });
          if (!zslRes.ok) {
            const error = await zslRes.json();
            const statusCode = zslRes.status;
            // Use setMessagesAtom instead of sendMessageAtom
            addError(error, {
              source: "executeZslAndSaveSetter",
              context: {
                name: "zsl_execution_error",
                zsl: processedZsl,
                statusCode,
              },
            });

            // Add error message with execute_zsl tool call to thread
            const errorMessage = error.message || JSON.stringify(error);
            addMessageToThread(
              composerService,
              true,
              errorMessage,
              processedZsl,
            );

            return;
          }

          const zsl = await zslRes.json();
          set(isViewOnlyModeAtom, false);

          const canvasIsNotEmpty = Object.keys(zsl.data.canvases).length > 0;

          if (canvasIsNotEmpty) {
            // Set the flag before navigating
            set(programmaticNavigationExpectedAtom, true);
            // Defer navigation with a slightly longer delay
            setTimeout(() => {
              window.location.href = `/app/canvas/${Object.keys(zsl.data.canvases)[0]}?tour=true`;
            }, 100); // Increased delay to 100ms
          }
        } catch (error) {
          const statusCode = error instanceof Response ? error.status : 500;
          set(handleZslExecutionErrorSetter, {
            error,
            statusCode,
            prompt,
            zsl: processedZsl,
          });

          const errorMessage =
            error instanceof Error
              ? error.message
              : "Unknown error executing ZSL";

          // Use setMessagesAtom and handleSubmitAtom instead of sendMessageAtom
          addMessageToThread(composerService, true, errorMessage, processedZsl);

          addError(error, {
            source: "executeZslAndSaveSetter",
            context: {
              name: "zsl_execution_error",
            },
          });

          // Reset the flag on error
          set(programmaticNavigationExpectedAtom, false);
        } finally {
          set(isExecutingZslAtom, false);
        }
      }
    } else {
      // Exclude canvases since CreateCanvasFromTemplateToolbar, AiCopilotBanner already has canvas
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { canvases, ...zslNoCanvas } = zsl!;
      try {
        const zslRes = await fetch("/api/canvasui/zsl", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            template: {
              meta: {},
              ...zslNoCanvas,
            },
          }),
        });
        if (!zslRes.ok) {
          const error = await zslRes.json();
          const statusCode = zslRes.status;

          set(handleZslExecutionErrorSetter, {
            error,
            statusCode,
            prompt,
            zsl: zslNoCanvas,
          });

          const errorMessage = error.message || JSON.stringify(error);
          addMessageToThread(composerService, true, errorMessage, zslNoCanvas);

          addError(error, {
            source: "executeZslAndSaveSetter",
            context: {
              name: "zsl_execution_error",
              zsl: zslNoCanvas,
              statusCode,
            },
          });

          return;
        }

        const result: ZSLFromTemplate = await zslRes.json();

        const canvas = get(canvasAtom);
        const nodes = get(nodesAtom);
        const edges = get(edgesAtom);

        const hydratedCanvas = assignAssetsAndAttributionsToCanvas(
          { ...canvas, nodes, edges }, // Pass in current nodes & edges
          result,
          prompt,
        );

        /* Clearing out template now that it's been used so saving nodes and edges will persist.

        This needs to go before setting the nodes so that zapAtomFamily_SUSPENDS will make a fetch call with the real zap ids or else it will try to find the zap in the template */
        set(zslAtom, undefined);
        set(nodesAtom, hydratedCanvas.nodes);
        set(edgesAtom, hydratedCanvas.edges);

        const canvasToSave: CanvasToSave = {
          nodes: hydratedCanvas.nodes,
          edges: hydratedCanvas.edges,
        };

        const newCanvasName = canvas.name
          ? undefined
          : removeTemplateFromString(zslNoCanvas.name);

        if (newCanvasName) {
          // Only set the new zsl name as canvas name when user hasn't named their canvas
          set(canvasAtom, (prevCanvas) => ({
            ...prevCanvas,
            name: newCanvasName,
          }));
          canvasToSave.name = newCanvasName;
        }

        const newCanvasDescription = canvas.description
          ? undefined
          : zslNoCanvas.description;

        if (newCanvasDescription) {
          // Only set the new zsl description as canvas description when user hasn't added a description
          set(canvasAtom, (prevCanvas) => ({
            ...prevCanvas,
            description: newCanvasDescription,
          }));
          canvasToSave.description = newCanvasDescription;
        }

        set(showCanvasTemplateWidgetAtom, false);
        set(isViewOnlyModeAtom, false);
        set(saveCanvasSetter, canvasToSave);
        set(streamingProgressAtom, 0);

        // Send success message
        addMessageToThread(composerService, false, undefined, zslNoCanvas);

        set(startTourSetter, {
          // newCanvasName will be undefined when user has added a name - match above and show inputted name first
          canvasName: newCanvasName || canvas.name,
          // newCanvasDescription will be undefined when user has added a description - match above and show inputted description first
          canvasDescription: newCanvasDescription || canvas.description,
          nodes: hydratedCanvas.nodes,
          edges: hydratedCanvas.edges,
          composerHandleSubmit: composerService.handleSubmit,
        });
      } catch (error) {
        const statusCode = error instanceof Response ? error.status : 500;
        set(handleZslExecutionErrorSetter, {
          error,
          statusCode,
          prompt,
          zsl: zslNoCanvas,
        });

        const errorMessage =
          error instanceof Error
            ? error.message
            : "Unknown error executing ZSL";

        // Use setMessagesAtom and handleSubmitAtom instead of sendMessageAtom
        addMessageToThread(composerService, true, errorMessage, zslNoCanvas);

        addError(error, {
          source: "executeZslAndSaveSetter",
          context: {
            name: "zsl_execution_error",
            canvas: {
              message: "ZSL execution error - network or fetch failure",
              zsl: zslNoCanvas,
              statusCode,
              context: "Network or fetch error",
            },
          },
        });
      } finally {
        set(isExecutingZslAtom, false);
      }
    }
  },
);

executeZslAndSaveSetter.debugLabel = "executeZslAndSaveSetter";
